import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ButtonIcon, useToastContext } from '@resideo/blueprint-oneportal';
import type { CameraV2Operation } from '@resideo/web-integration-services-api-client';
import { chevronLeftIcon, informationLineIcon } from '@/assets/icons';
import { createRisClient } from '@/api/risClient';
import { getApiUrl } from '@/api/apiConfig';
import { apiService } from '@/services/apiService';
import styles from './ReviewApplyPage.module.css';

export interface BulkChange {
  label: string;
  value: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const ReviewApplyPage: React.FC<{ api?: any }> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { addToast } = useToastContext();
  const [isApplying, setIsApplying] = useState(false);

  const state = location.state as { changes?: BulkChange[]; count?: number; nonTrumanCameraCount?: number; cameraOperations?: CameraV2Operation[] } | null;
  const cameraIds = (searchParams.get('cameras') ?? '').split(',').filter(Boolean);
  const count = state?.count ?? cameraIds.length;
  const changes: BulkChange[] = state?.changes ?? [];
  const cameraOperations: CameraV2Operation[] = state?.cameraOperations ?? [];
  // Fenix/VX cameras in the selection don't support these Truman-only
  // settings, so every applied change gets a "not supported" notice for them.
  const nonTrumanCameraCount = state?.nonTrumanCameraCount ?? 0;

  const handleApply = async () => {
    if (cameraIds.length === 0 || cameraOperations.length === 0) {
      addToast({ toastType: 'Error', message: t('bulkEditSettings.review.applyError') });
      return;
    }
    setIsApplying(true);
    try {
      await apiService.getAuthToken();
      const risClient = createRisClient(
        getApiUrl('risPublicApi'),
        () => localStorage.getItem('oneportal_auth_token') ?? localStorage.getItem('authToken'),
      );
      await risClient.batch.batchProcessCameraBatchOperations({
        deviceIds: cameraIds,
        cameraOperations,
      });
      navigate('/devices');
    } catch {
      addToast({ toastType: 'Error', message: t('bulkEditSettings.review.applyError') });
    } finally {
      setIsApplying(false);
    }
  };

  const cameraText = count === 1
    ? t('bulkEditSettings.review.descSingular')
    : t('bulkEditSettings.review.descPlural', { count });

  const pageTitle = count === 1
    ? t('bulkEditSettings.pageTitle', { count })
    : t('bulkEditSettings.pageTitlePlural', { count });

  return (
    <div className={styles.page}>
      <div className={styles.topHeader}>
        <ButtonIcon size="small" iconType="icon" aria-label={t('common.back')} onClick={() => navigate(-1)}>
          <img src={chevronLeftIcon} width="20" height="20" alt="" aria-hidden="true" />
        </ButtonIcon>
        <h2 className={styles.topTitle}>{pageTitle}</h2>
      </div>

      <div className={styles.body}>
        <div className={[styles.header, styles.centeredHeader].join(' ')}>
          <div>
            <h1 className={styles.title}>{t('bulkEditSettings.review.title')}</h1>
            <p className={styles.desc}>
              {cameraText} {t('bulkEditSettings.review.descSuffix')}
            </p>
          </div>
          <Button variant="secondary" size="small" onClick={() => navigate(-1)}>
            {t('common.edit')}
          </Button>
        </div>

        {changes.length > 0 && nonTrumanCameraCount > 0 && (
          <div className={styles.unsupportedNotices}>
            {changes.map((change, i) => (
              <div className={styles.unsupportedNotice} key={i}>
                <img src={informationLineIcon} width="18" height="18" alt="" aria-hidden="true" />
                <span>
                  {t('bulkEditSettings.review.unsupportedNotice', {
                    label: change.label,
                    count: nonTrumanCameraCount,
                  })}
                </span>
              </div>
            ))}
          </div>
        )}

        {changes.length > 0 && (
          <div className={styles.changesBox}>
            {changes.map((change, i) => (
              <div className={styles.changeRow} key={i}>
                <div className={styles.changeLabel}>{change.label}</div>
                <div className={styles.changeValue}>{change.value}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <Button
          className={styles.footerButtonSecondary}
          variant="secondary"
          size="small"
          onClick={() => navigate(-1)}
        >
          {t('common.back')}
        </Button>
        <Button
          className={styles.footerButtonPrimary}
          variant="primary"
          size="small"
        disabled={isApplying || cameraIds.length === 0 || cameraOperations.length === 0}
          onClick={handleApply}
        >
          {isApplying ? t('common.applying') : t('common.apply')}
        </Button>
      </div>
    </div>
  );
};

export default ReviewApplyPage;
